'use client'

import { ForwardedRef, RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useInView } from 'framer-motion'
import { usePathname } from '@/routing'
import { getCSSCustomPropertyValue } from '@/shared/utils/common'
import { throttle } from './lodash'

export const useScrollEvent = (callback: () => void, withoutThrottle = false) => {
  useLayoutEffect(() => {
    const handleScroll = withoutThrottle ? callback : throttle(callback, 16)

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [callback])
}

export const variantsDefault = {
  visible: { opacity: 1, translateY: 0, transition: { duration: 0.4 } },
  hidden: { opacity: 0, translateY: 30 },
}

export const containerVariants = {
  hidden: { opacity: 0, translateY: 0 },
  visible: {
    opacity: 1,
    translateY: 0,
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.3,
    },
  },
}

export const itemVariants = {
  hidden: { translateY: 30, opacity: 0 },
  visible: {
    translateY: 0,
    opacity: 1,
    transition: { duration: 0.42 },
  },
}

export function useAppearance(refEx?: RefObject<HTMLDivElement> | null | ForwardedRef<HTMLDivElement>) {
  const ref = useRef(null)
  const isInView = useInView((refEx as RefObject<Element>) || ref, { once: true, margin: '-1% 0%' })
  const animate = isInView ? 'visible' : 'hidden'

  return { ref, animate, isInView }
}

const debounce = (func: () => void, delay: number) => {
  let timeoutId: NodeJS.Timeout
  return () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(func, delay)
  }
}

export function useScaleSection(loaded = false, threshold = 0.5) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const startPositionRef = useRef<number>(0)

  const handleResize = debounce(() => {
    const startSectionPosition = sectionRef.current?.getBoundingClientRect().top
    const isLargeScreen = window.innerWidth >= 1920
    const mainPaddingSize =
      parseFloat(getCSSCustomPropertyValue('--main-padding')) * (isLargeScreen ? window.innerWidth / 100 : 1)

    const initScale = 1 - (mainPaddingSize * 2) / window.innerWidth

    sectionRef.current?.style.setProperty('--startScaleX', initScale.toString())
    startPositionRef.current = Number(startSectionPosition).valueOf()

    const startSectionPosition2 = (startPositionRef.current ?? 0) - window.innerHeight
    const viewportHeight = window.innerHeight
    const scrollTop = window.scrollY - startSectionPosition2
    const ratio = Math.min(scrollTop / (viewportHeight * threshold), 1)

    sectionRef.current?.style.setProperty('--ratio', ratio.toString())
  }, 300)

  useLayoutEffect(() => {
    handleResize()

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useScrollEvent(() => {
    if (!loaded) return
    const startSectionPosition = (startPositionRef.current ?? 0) - window.innerHeight
    const viewportHeight = window.innerHeight
    const scrollTop = window.scrollY - startSectionPosition
    const ratio = Math.min(scrollTop / (viewportHeight * threshold), 1)

    sectionRef.current?.style.setProperty('--ratio', ratio.toString())
  }, true)

  return sectionRef
}

export const useHeaderChangeColor = () => {
  const pathname = usePathname()

  useLayoutEffect(() => {
    const whiteSectionsEls = document.querySelectorAll('.white-section-js')
    const blueSections = document.querySelectorAll('.blue-section-js')
    const headerEl = document.querySelector('#header')

    if (!headerEl || !whiteSectionsEls || !blueSections) return

    const observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach(entry => {
          const target = entry.target as Element
          const isBlue = target.classList.contains('blue-section-js')
          const isWhite = target.classList.contains('white-section-js')

          // для белых секций
          if (isWhite) {
            headerEl.classList.toggle('hasIntersection', entry.isIntersecting)
          }
          // для красных секций
          if (isBlue) {
            headerEl.classList.toggle('hasIntersectionBlue', entry.isIntersecting)
          }
        })
      },
      { threshold: 0, rootMargin: '0px 0px -95% 0px' }
    )

    whiteSectionsEls.forEach(el => observer.observe(el))
    blueSections.forEach(el => observer.observe(el))

    return () => {
      whiteSectionsEls.forEach(entry => {
        observer.unobserve(entry)
      })
      blueSections.forEach(el => observer.unobserve(el))

      if (headerEl.classList.contains('hasIntersection')) {
        headerEl.classList.remove('hasIntersection')
      }
      if (headerEl.classList.contains('hasIntersectionBlue')) {
        headerEl.classList.remove('hasIntersectionBlue')
      }
    }
  }, [pathname])
}

interface useIntersection {
  ref?: RefObject<HTMLUListElement>
  onCurrentPage?: (page: number, observerInstance: IntersectionObserver) => void
  options?: IntersectionObserverInit
  isOnce?: boolean
}

export function useIntersection({ ref, onCurrentPage, options, isOnce = false }: useIntersection) {
  useEffect(() => {
    if (!ref?.current) return

    const observer = new IntersectionObserver(([entry], observerInstance) => {
      // Если элемент полностью в области просмотра
      if (entry.isIntersecting) {
        const listEl = entry.target as HTMLUListElement
        const currentPage = Number(listEl.dataset.index ?? 0) + 1

        onCurrentPage?.(currentPage, observerInstance)
        if (isOnce) {
          observerInstance.disconnect()
        }
      }
    }, options)

    observer.observe(ref.current)

    return () => {
      observer.disconnect()
    }
  }, [])
}

export function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(false)

  useEffect(() => {
    const media = window.matchMedia(query)
    if (media.matches !== matches) {
      setMatches(media.matches)
    }

    const listener = () => {
      setMatches(media.matches)
    }

    media.addEventListener('change', listener)

    return () => {
      media.removeEventListener('change', listener)
    }
  }, [matches, query])

  return matches
}

export const useDebounce = (value: any, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => clearTimeout(timeout)
  }, [value, delay])

  return debouncedValue
}
